/* eslint-disable no-extra-boolean-cast */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import Tooltip from '@mui/material/Tooltip';
import {
  Search as SearchIcon,
  XCircle as XCircleIcon,
  CheckCircle as CheckCircleIcon,
} from 'react-feather';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import { useSnackbar } from 'notistack';

import useStyles from '../SubcategoriesListView/styles';
import alphabeticSort from '../../../utils/alphabeticSort';
import { activateSubcategory } from '../../../requests/api/subcategories';

function Results({
  className,
  subcategories,
  supermarkets,
  categories,
  setCategories,
  originalCategories,
  count,
  allSearchResults,
  page,
  limit,
  query,
  selectedSupermarkets,
  selectedCategories,
  setPage,
  setLimit,
  setQuery,
  setSelectedSupermarkets,
  setSelectedCategories,
  onActivate,
  ...rest
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [currentQuery, setCurrentQuery] = useState(query);
  const [currentCategories, setCurrentCategories] = useState(categories);
  const [currentSubcategories, setCurrentSubcategories] = useState(subcategories);
  const [activatingId, setActivatingId] = useState(null);

  useEffect(() => { setCurrentSubcategories(subcategories); }, [subcategories]);
  useEffect(() => { setCurrentQuery(query); }, [query]);

  const handleQueryChange = (event) => {
    event.persist();
    setCurrentQuery(event.target.value);
  };

  useEffect(() => {
    setCurrentCategories(categories);
  }, [categories]);

  const getAvailableSupermarkets = () => {
    // Usar los resultados actuales de la tabla para filtrar si hay búsqueda
    if (query && query.trim() !== '' && currentSubcategories.length > 0) {
      const uniqueSupermarketIds = [...new Set(currentSubcategories.map((sub) => sub.Supermarket?.id).filter(Boolean))];
      return supermarkets.filter((sup) => uniqueSupermarketIds.includes(sup.id)).sort((a, b) => alphabeticSort(a, b));
    }
    return supermarkets;
  };

  const getAvailableCategories = () => {
    // Usar los resultados actuales de la tabla para filtrar si hay búsqueda
    if (query && query.trim() !== '' && currentSubcategories.length > 0) {
      const uniqueCategoryIds = [...new Set(currentSubcategories.map((sub) => sub.Category?.id).filter(Boolean))];
      return originalCategories.filter((cat) => uniqueCategoryIds.includes(cat.id)).sort((a, b) => alphabeticSort(a, b));
    }
    return currentCategories;
  };

  const getValidatedSupermarketValue = () => {
    if (!selectedSupermarkets || selectedSupermarkets.length === 0) {
      return [];
    }
    const availableIds = getAvailableSupermarkets().map((sup) => sup.id);
    return selectedSupermarkets.filter((sup) => availableIds.includes(sup.id));
  };

  const getValidatedCategoryValue = () => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return [];
    }
    const availableIds = getAvailableCategories().map((cat) => cat.id);
    return selectedCategories.filter((cat) => availableIds.includes(cat.id));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(0);
  };

  const handleChange = (type, newVal) => {
    if (!!newVal) {
      const ides = newVal.map((val) => val.id);
      setPage(0);
      if (type === 'supermarket') {
        setSelectedSupermarkets(newVal);
        setSelectedCategories([]);
        if (ides.length === 0) {
          setCategories(originalCategories);
        } else {
          setCategories(
            originalCategories.filter((item) => ides.includes(item.supermarket_id)).sort((a, b) => alphabeticSort(a, b)),
          );
        }
      } else if (type === 'category') {
        setSelectedCategories(newVal);
      }
    }
  };

  const handleActivate = async (subcategoryId) => {
    setActivatingId(subcategoryId);
    try {
      const response = await activateSubcategory(subcategoryId);
      if (response.success) {
        enqueueSnackbar('Subcategoría activada exitosamente', {
          variant: 'success',
        });
        // Reload the data by triggering useEffect
        if (onActivate) {
          onActivate();
        }
      } else {
        enqueueSnackbar(response.message || 'Error al activar la subcategoría', {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar('Error al activar la subcategoría', {
        variant: 'error',
      });
    } finally {
      setActivatingId(null);
    }
  };

  // Siempre mostrar el componente, incluso si no hay resultados

  return (
    <Card
      className={clsx(classes.rootResults, className)}
      {...rest}
    >
      <Divider />
      <Box
        p={2}
        minHeight={56}
      >
        <Grid container spacing={1} >
          <Grid item xs={2} md={4}>
            <TextField
              className={classes.queryField}
              onChange={handleQueryChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon
                      fontSize="small"
                      color="action"
                    >
                      <SearchIcon />
                    </SvgIcon>
                  </InputAdornment>
                ),
                endAdornment: currentQuery ? (
                  <IconButton size="small" onClick={() => {
                    setCurrentQuery('');
                    setQuery('');
                    setPage(0);
                  }}>
                    <XCircleIcon />
                  </IconButton>
                ) : null,
              }}
              placeholder="Buscar por Subcategoría"
              value={currentQuery}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={0.5} md={0.5}>
            <Button
              color="secondary"
              variant="contained"
              className={classes.searchAction}
              onClick={() => {
                setQuery(currentQuery);
                setPage(0);
              }}
            >
              <SvgIcon >
                <SearchIcon />
              </SvgIcon>
            </Button>
          </Grid>
          <Grid item xs={2} md={4.5} align='right'>
            {getAvailableSupermarkets().length > 0 && <Autocomplete
              id="supermarkets-autocomplete"
              className={classes.options}
              options={getAvailableSupermarkets()}
              multiple
              value={getValidatedSupermarketValue()}
              getOptionLabel={(option) => option.name}
              onChange={(e, v) => handleChange('supermarket', v)}
              limitTags={1}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.queryField}
                  label='Supermercados'
                  variant='outlined'
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              )}
            />
            }
          </Grid>
          <Grid item xs={3} justifySelf="end">
            {getAvailableCategories().length > 0 && <Autocomplete
              id="categories-autocomplete"
              options={getAvailableCategories()}
              multiple
              value={getValidatedCategoryValue()}
              getOptionLabel={(option) => option.name}
              onChange={(e, v) => handleChange('category', v)}
              limitTags={1}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.queryField}
                  label='Categoría'
                  variant='outlined'
                />
              )}
              renderOption={(props, option, { selected }) => {
                const supermarket = getAvailableSupermarkets().find((sup) => sup.id === option.supermarket_id);
                return (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name} {supermarket ? `(${supermarket.name})` : ''}
                  </li>
                );
              }}
            />
            }
          </Grid>
        </Grid>
      </Box>

      <PerfectScrollbar>
        <Box minWidth={700}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align='center'>
                  Subcategoría
                </TableCell>
                <TableCell align='center'>
                  Categoría asociada
                </TableCell>
                <TableCell align='center'>
                  Supermercado
                </TableCell>
                <TableCell align='center'>
                  Productos asociados
                </TableCell>
                <TableCell align='center'>
                  Frecuencia
                </TableCell>
                <TableCell align='center'>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentSubcategories.map((subcategory) => (
                  <TableRow
                    hover
                    key={subcategory.id}
                  >
                    <TableCell>{subcategory.name}</TableCell>
                    <TableCell>{subcategory.Category.name}</TableCell>
                    <TableCell align='center'>{subcategory.Supermarket.name}</TableCell>
                    <TableCell align='center'>{subcategory.Products.length}</TableCell>
                    <TableCell align='center'>{subcategory.frequency}</TableCell>
                    <TableCell align='center'>
                      <Tooltip title="Activar Subcategoría">
                        <IconButton
                          onClick={() => handleActivate(subcategory.id)}
                          disabled={activatingId === subcategory.id}
                        >
                          <SvgIcon fontSize="small" color={activatingId === subcategory.id ? 'disabled' : 'primary'}>
                            <CheckCircleIcon />
                          </SvgIcon>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      <TablePagination
        component="div"
        count={count}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={Math.min(page, Math.max(0, Math.ceil(count / limit) - 1))}
        rowsPerPage={limit}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Filas por página"
      />
    </Card>
  );
}

Results.propTypes = {
  className: PropTypes.string,
  subcategories: PropTypes.array,
  count: PropTypes.number,
  supermarkets: PropTypes.array,
  originalCategories: PropTypes.array,
  categories: PropTypes.array,
  setCategories: PropTypes.func,
  allSearchResults: PropTypes.array,
  page: PropTypes.number,
  limit: PropTypes.number,
  query: PropTypes.string,
  selectedSupermarkets: PropTypes.array,
  selectedCategories: PropTypes.array,
  setPage: PropTypes.func,
  setLimit: PropTypes.func,
  setQuery: PropTypes.func,
  setSelectedSupermarkets: PropTypes.func,
  setSelectedCategories: PropTypes.func,
  onActivate: PropTypes.func,
};

Results.defaultProps = {
  subcategories: [],
};

export default Results;
